import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, } from '@/shared/components/Modal';
import { Button } from '@/shared/components/Button';
import { Field } from '@/shared/components/Field';
import { Input } from '@/shared/components/Input';
import { MoneyInput } from '@/shared/components/MoneyInput';
import { Select } from '@/shared/components/Select';
import { useToast } from '@/shared/components/Toast';
import { AppError } from '@/core/errors/AppError';
import { useAplicarDescuento } from '@/data/hooks/usePagos';
const ORIGENES = ['BANCO', 'ESCUELA', 'PROMOTOR', 'PROMOCION', 'OTRO'];
const AplicarSchema = z
    .object({
    origen: z.enum(ORIGENES),
    descripcion: z.string().min(1, 'Descripción obligatoria').max(200),
    pct: z.string().optional().or(z.literal('')),
    monto: z.string().optional().or(z.literal('')),
})
    .refine((v) => {
    const hasPct = v.pct && v.pct !== '';
    const hasMonto = v.monto && v.monto !== '';
    return (hasPct || hasMonto) && !(hasPct && hasMonto);
}, {
    path: ['pct'],
    message: 'Indicá porcentaje O monto (no ambos).',
});
export function AplicarDescuentoModal({ open, onOpenChange, reservaId }) {
    const toast = useToast();
    const aplicar = useAplicarDescuento(reservaId);
    const [tipo, setTipo] = useState('pct');
    const form = useForm({
        resolver: zodResolver(AplicarSchema),
        defaultValues: { origen: 'ESCUELA', descripcion: '', pct: '', monto: '' },
    });
    const errors = form.formState.errors;
    const onSubmit = form.handleSubmit((v) => {
        aplicar.mutate({
            origen: v.origen,
            descripcion: v.descripcion,
            pct: tipo === 'pct' && v.pct ? Number(v.pct) : undefined,
            monto: tipo === 'monto' && v.monto ? Number(v.monto) : undefined,
        }, {
            onSuccess: () => {
                toast.exito('Descuento aplicado');
                onOpenChange(false);
                form.reset();
            },
            onError: (err) => toast.error(err instanceof AppError ? err.mensaje : 'No pudimos aplicarlo.'),
        });
    });
    return (_jsx(Modal, { open: open, onOpenChange: onOpenChange, children: _jsxs(ModalContent, { size: "sm", children: [_jsx(ModalHeader, { titulo: "Aplicar descuento" }), _jsxs("form", { onSubmit: onSubmit, noValidate: true, children: [_jsxs(ModalBody, { className: "flex flex-col gap-4", children: [_jsx(Field, { label: "Origen", htmlFor: "origen", error: errors.origen?.message, required: true, children: _jsx(Select, { id: "origen", ...form.register('origen'), children: ORIGENES.map((o) => (_jsx("option", { value: o, children: o }, o))) }) }), _jsx(Field, { label: "Descripci\u00F3n", htmlFor: "descripcion", error: errors.descripcion?.message, required: true, children: _jsx(Input, { id: "descripcion", autoFocus: true, placeholder: "Ej. Promo Banco Naci\u00F3n", ...form.register('descripcion') }) }), _jsxs("div", { children: [_jsx("div", { className: "mb-1 text-sm font-semibold text-text-primary", children: "Tipo de descuento" }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { type: "button", variant: tipo === 'pct' ? 'primary' : 'secondary', size: "sm", onClick: () => setTipo('pct'), children: "Porcentaje" }), _jsx(Button, { type: "button", variant: tipo === 'monto' ? 'primary' : 'secondary', size: "sm", onClick: () => setTipo('monto'), children: "Monto fijo" })] })] }), tipo === 'pct' ? (_jsx(Field, { label: "Porcentaje", htmlFor: "pct", error: errors.pct?.message, required: true, children: _jsx(Input, { id: "pct", inputMode: "decimal", placeholder: "15", ...form.register('pct') }) })) : (_jsx(Field, { label: "Monto", htmlFor: "monto", error: errors.monto?.message, required: true, children: _jsx(MoneyInput, { id: "monto", value: form.watch('monto') ?? '', onChange: (v) => form.setValue('monto', v, { shouldDirty: true, shouldValidate: true }) }) }))] }), _jsxs(ModalFooter, { children: [_jsx(Button, { type: "button", variant: "secondary", onClick: () => onOpenChange(false), disabled: aplicar.isPending, children: "Cancelar" }), _jsx(Button, { type: "submit", cargando: aplicar.isPending, children: "Aplicar" })] })] })] }) }));
}
